/**
 * PostHog product analytics for the portal.
 *
 * Separate from CLI product telemetry (`chi telemetry` / `GET /v1/telemetry/*`):
 * this tracks marketing and account-portal funnels in PostHog. It is optional —
 * with no project token configured every call is a no-op, so local development
 * and prerender stay quiet.
 *
 * Initialisation is deferred to the browser and happens once. The SSR prerender
 * path imports Providers, so this module must never call `posthog.init` at
 * load time or pull the SDK into Node.
 */

import { env } from '@/app/env'

type Properties = Record<string, string | number | boolean | null | undefined>

interface AnalyticsClient {
  capture: (event: string, properties?: Properties) => void
  identify: (distinctId: string, properties?: Properties) => void
  reset: () => void
  group: (type: string, key: string) => void
}

const noop: AnalyticsClient = {
  capture() {},
  identify() {},
  reset() {},
  group() {},
}

let client: AnalyticsClient = noop
let initPromise: Promise<AnalyticsClient> | null = null

function compact(properties?: Properties): Record<string, string | number | boolean> | undefined {
  if (!properties) return undefined
  const out: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null) continue
    out[key] = value
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * Portal routes: signed-in pages that render credentials, one-time codes, billing
 * and team data. Autocapture records the text of whatever was clicked (`$el_text`),
 * and a click on the machine-token panel once sent a whole `chi_mt_…` secret to
 * PostHog, so interaction capture is off here entirely. Marketing routes keep it.
 * Explicit `capture(...)` calls and pageviews still go through, scrubbed below.
 */
const PORTAL_PATH =
  /^\/(machine-tokens|cli|invitations|account|billing|devices|team|support|downloads)(\/|$)/

/** Matches a portal route anywhere in a full URL, for the SDK's own ignorelist. */
const PORTAL_URL =
  /^[a-z]+:\/\/[^/]+\/(machine-tokens|cli|invitations|account|billing|devices|team|support|downloads)([/?#]|$)/i

/** Events built from the DOM the person interacted with. */
const INTERACTION_EVENTS = new Set(['$autocapture', '$rageclick', '$dead_click', '$copy_autocapture'])

/**
 * Query parameters that carry one-time values: the CLI's `user_code` and `session`,
 * an OAuth `code` and `state`, anything called `token`. Matched raw (`?code=`) and
 * percent-encoded inside another parameter (`?next=%2Fcli%2Fauthorize%3Fsession%3D…`).
 */
const SECRET_PARAM = /([?&]|%3F|%26)(user_code|code|session|state|token)(=|%3D)[^&#%]*(%[0-9A-F]{2}[^&#%]*)*/gi
/** The invitation token is a path segment, `/invitations/<token>`, possibly encoded. */
const INVITATION_PATH = /(\/|%2F)invitations(\/|%2F)[^/?#&%]+/gi
/** A machine, SCIM or CI token, wherever it turns up. */
const API_TOKEN = /\bchi_(mt|scim|ci)_[A-Za-z0-9_-]+/g

export function scrubString(value: string): string {
  return value
    .replace(API_TOKEN, 'chi_$1_[redacted]')
    .replace(INVITATION_PATH, '$1invitations$2[redacted]')
    .replace(SECRET_PARAM, '$1$2$3[redacted]')
}

function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubString(value)
  if (Array.isArray(value)) return value.map(scrubValue)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, inner] of Object.entries(value)) out[key] = scrubValue(inner)
    return out
  }
  return value
}

type CaptureResult = {
  event: string
  properties: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
}

/**
 * Last line before an event leaves the browser: drops interaction events on portal
 * routes (in case an element slips past the ignorelist) and scrubs every string on
 * every other event, `$current_url`, `$referrer` and `$elements` included.
 */
export function beforeSend<T extends CaptureResult>(event: T | null): T | null {
  if (!event) return event
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  if (INTERACTION_EVENTS.has(event.event) && PORTAL_PATH.test(pathname)) return null
  event.properties = scrubValue(event.properties) as T['properties']
  if (event.$set) event.$set = scrubValue(event.$set) as T['$set']
  if (event.$set_once) event.$set_once = scrubValue(event.$set_once) as T['$set_once']
  return event
}

/**
 * Loads and initialises PostHog once in the browser when a project token is set.
 * Safe to call repeatedly; concurrent callers share the same promise.
 */
export function ensureAnalytics(): Promise<AnalyticsClient> {
  if (typeof window === 'undefined' || !env.posthog.key) {
    return Promise.resolve(noop)
  }
  if (client !== noop) {
    return Promise.resolve(client)
  }
  if (initPromise) {
    return initPromise
  }

  initPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(env.posthog.key, {
        api_host: env.posthog.host,
        // Sep 2026 SDK defaults (autocapture, persistence, etc.).
        defaults: '2026-05-30',
        // No interaction capture on portal routes; `.ph-no-capture` on secrets as well.
        // A custom ignorelist replaces the SDK's defaults, so they are listed again.
        autocapture: {
          url_ignorelist: [PORTAL_URL],
          css_selector_ignorelist: ['.ph-no-capture', '.ph-no-autocapture', '[data-ph-no-autocapture]'],
          capture_copied_text: false,
        },
        before_send: beforeSend,
        // Anonymous traffic stays personless until identify — matches privacy posture.
        person_profiles: 'identified_only',
        // SPA navigations are captured explicitly so prerendered routes do not double-count.
        capture_pageview: false,
        capture_pageleave: true,
        // Session replay stays off until counsel signs off on cookies / EU consent.
        disable_session_recording: true,
      })
      // Team browsers opt out once by opening any page with ?internal=1, and back in
      // with ?internal=0. PostHog persists the choice, so it holds when signed out,
      // where the internal-users cohort (matched on email) cannot see the visit.
      const internal = new URLSearchParams(window.location.search).get('internal')
      if (internal === '1') posthog.opt_out_capturing()
      else if (internal === '0') posthog.opt_in_capturing()
      client = {
        capture(event, properties) {
          posthog.capture(event, compact(properties))
        },
        identify(distinctId, properties) {
          posthog.identify(distinctId, compact(properties))
        },
        reset() {
          posthog.reset()
        },
        group(type, key) {
          posthog.group(type, key)
        },
      }
      return client
    })
    .catch(() => {
      initPromise = null
      return noop
    })

  return initPromise
}

export function capture(event: string, properties?: Properties): void {
  void ensureAnalytics().then((analytics) => analytics.capture(event, properties))
}

export function identify(distinctId: string, properties?: Properties): void {
  void ensureAnalytics().then((analytics) => analytics.identify(distinctId, properties))
}

export function resetAnalytics(): void {
  client.reset()
}

export function capturePageview(pathname: string): void {
  // Path only — never raw `location.search`. Invite tokens and similar query
  // values must not land in a third-party analytics store.
  // The path can carry one too — `/invitations/<token>` — so it is scrubbed here, and
  // again with every other property in `beforeSend`.
  pathname = scrubString(pathname)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  capture('$pageview', {
    $current_url: origin ? `${origin}${pathname}` : pathname,
    path: pathname,
  })
}
